import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches } from "@/lib/employeeSearch";
import { toast } from "sonner";
import { Search, BookOpen, ArrowLeft } from "lucide-react";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

const STATUS_CLS = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    accountant_verified: "bg-sky-50 text-sky-700 border-sky-200",
    dept_approved: "bg-violet-50 text-violet-700 border-violet-200",
    finance_approved: "bg-blue-50 text-blue-700 border-blue-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    disbursed: "bg-indigo-50 text-indigo-700 border-indigo-200",
    completed: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    cancelled: "bg-ink/5 text-ink/70 border-ink/10",
};

function StatusPill({ status }) {
    const cls = STATUS_CLS[status] || "bg-ink/5 text-ink/70 border-ink/10";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${cls}`}>
            {(status || "").replace(/_/g, " ")}
        </span>
    );
}

export default function LoanLedgerReport({ selfOnly = false }) {
    const { user } = useAuth();
    const role = user?.role || "";
    const showPicker = !selfOnly && role !== "employee";

    const [employees, setEmployees] = useState([]);
    const [selectedEmp, setSelectedEmp] = useState(selfOnly ? user?.employee_id : "");
    const [empSearch, setEmpSearch] = useState("");
    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(new Set());

    useEffect(() => {
        if (!showPicker) return;
        (async () => {
            try {
                const { data } = await api.get("/employees");
                setEmployees(data || []);
            } catch {/* ignore */}
        })();
    }, [showPicker]);

    useEffect(() => {
        const id = selfOnly ? user?.employee_id : selectedEmp;
        if (!id) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/employees/${id}/loan-ledger`);
                if (!cancelled) setLedger(data);
            } catch (e) {
                if (!cancelled) {
                    toast.error(formatApiError(e.response?.data?.detail) || "Failed to load loan ledger");
                    setLedger(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selfOnly, user?.employee_id, selectedEmp]);

    const empQ = empSearch.trim().toLowerCase();
    const filteredEmps = empQ
        ? employees.filter((e) => employeeMatches(e, empQ)).slice(0, 50)
        : employees.slice(0, 50);

    const toggle = (id) => {
        const n = new Set(expanded);
        if (n.has(id)) n.delete(id); else n.add(id);
        setExpanded(n);
    };

    const downloadPDF = () => {
        if (!ledger || !ledger.loans) return;
        const lines = [];
        lines.push(`Loan & Advance Ledger`);
        lines.push(`Employee: ${ledger.employee.full_name} (${ledger.employee.employee_no || "—"})`);
        lines.push(`Department: ${ledger.employee.department || "—"}`);
        lines.push(`Generated: ${new Date().toLocaleString()}`);
        lines.push("");
        lines.push(`Total borrowed: ${fmt(ledger.summary.total_borrowed)}`);
        lines.push(`Total outstanding: ${fmt(ledger.summary.total_outstanding)}`);
        lines.push(`Open loans: ${ledger.summary.open_count} · Completed: ${ledger.summary.completed_count}`);
        lines.push("");
        lines.push("Type,Amount,Balance,Monthly,Tenure,Status,Created,Reason");
        ledger.loans.forEach((l) => {
            lines.push([
                l.type || "loan",
                l.amount || 0,
                l.balance || 0,
                l.monthly_installment || 0,
                l.tenure_months || 1,
                l.status || "",
                l.created_at || "",
                (l.reason || "").replace(/,/g, ";"),
            ].join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `loan_ledger_${(ledger.employee.full_name || "employee").replace(/\s+/g, "_")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV ledger downloaded");
    };

    const headerTitle = selfOnly ? "My Loan & Advance Ledger" : "Loan / Advance Ledger Report";
    const headerSubtitle = selfOnly
        ? "Your full loan and salary advance history, with installment-level visibility."
        : "View the loan & advance history of any employee, including installment schedule.";

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="loan-ledger-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Finance</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02] flex items-center gap-3">
                        <BookOpen className="w-9 h-9 text-klein" strokeWidth={1.5} /> {headerTitle}
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-2xl">{headerSubtitle}</p>
                </div>
                {ledger && ledger.loans.length > 0 && (
                    <button
                        onClick={downloadPDF}
                        className="inline-flex items-center gap-2 border border-klein text-klein px-4 py-2.5 text-sm hover:bg-klein hover:text-white"
                        data-testid="loan-ledger-download"
                    >
                        Download CSV
                    </button>
                )}
            </div>

            {showPicker && (
                <div className="mt-10 bg-white border border-ink/10 p-5" data-testid="loan-ledger-employee-picker">
                    <label className="overline block mb-2">Choose employee</label>
                    {selectedEmp && ledger?.employee ? (
                        <div className="flex items-center justify-between border border-klein bg-klein/5 px-3 py-2.5">
                            <div className="text-sm">
                                <div className="font-medium text-ink">{ledger.employee.full_name}</div>
                                <div className="text-xs text-ink/60">{ledger.employee.employee_no} · {ledger.employee.department || "—"}</div>
                            </div>
                            <button
                                onClick={() => { setSelectedEmp(""); setLedger(null); }}
                                className="text-xs uppercase tracking-[0.12em] text-ink/60 hover:text-ink"
                                data-testid="loan-ledger-employee-clear"
                            >
                                Change <ArrowLeft className="w-3 h-3 inline rotate-180" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                                <input
                                    type="search"
                                    value={empSearch}
                                    onChange={(e) => setEmpSearch(e.target.value)}
                                    placeholder="Search name, staff no, department, division, phone…"
                                    className="w-full border border-ink/15 py-2.5 pl-9 pr-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="loan-ledger-employee-search"
                                />
                            </div>
                            <div className="mt-2 max-h-64 overflow-y-auto border border-ink/10" data-testid="loan-ledger-employee-list">
                                {filteredEmps.length === 0 ? (
                                    <div className="px-3 py-4 text-xs text-ink/40">No matches.</div>
                                ) : filteredEmps.map((e) => (
                                    <button
                                        key={e.id}
                                        onClick={() => setSelectedEmp(e.id)}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-bone border-b border-ink/5 last:border-b-0"
                                        data-testid={`loan-ledger-employee-option-${e.id}`}
                                    >
                                        <div className="font-medium text-ink">{e.first_name} {e.last_name}</div>
                                        <div className="text-xs text-ink/50">{e.employee_no || "—"} · {e.department || "—"}</div>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {loading && <div className="mt-10 text-sm text-ink/40">Loading ledger…</div>}
            {!loading && ledger && (
                <div className="mt-10" data-testid="loan-ledger-content">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10 mb-6">
                        <div className="bg-white p-5"><div className="overline">Total borrowed</div><div className="mt-2 font-display font-black text-2xl text-ink tabular-nums">{fmt(ledger.summary.total_borrowed)}</div></div>
                        <div className="bg-white p-5"><div className="overline">Outstanding</div><div className="mt-2 font-display font-black text-2xl text-ink tabular-nums">{fmt(ledger.summary.total_outstanding)}</div></div>
                        <div className="bg-white p-5"><div className="overline">Open</div><div className="mt-2 font-display font-black text-2xl text-ink tabular-nums">{ledger.summary.open_count}</div></div>
                        <div className="bg-klein text-white p-5"><div className="overline !text-white/70">Completed</div><div className="mt-2 font-display font-black text-2xl tabular-nums">{ledger.summary.completed_count}</div></div>
                    </div>

                    {ledger.loans.length === 0 ? (
                        <div className="bg-white border border-ink/10 p-12 text-center text-sm text-ink/40" data-testid="loan-ledger-empty">
                            No loan or advance records found for this employee.
                        </div>
                    ) : (
                        <div className="bg-white border border-ink/10 overflow-auto">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b-2 border-ink/20 bg-bone">
                                        <th className="text-left overline px-4 py-3">Type</th>
                                        <th className="text-right overline px-4 py-3">Amount</th>
                                        <th className="text-right overline px-4 py-3 hidden md:table-cell">Monthly</th>
                                        <th className="text-right overline px-4 py-3 hidden md:table-cell">Tenure</th>
                                        <th className="text-right overline px-4 py-3">Balance</th>
                                        <th className="text-left overline px-4 py-3">Status</th>
                                        <th className="text-left overline px-4 py-3 hidden lg:table-cell">Created</th>
                                        <th className="overline px-2 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ledger.loans.map((l) => {
                                        const isOpen = expanded.has(l.id);
                                        return (
                                            <>
                                                <tr key={l.id} className="border-b border-ink/10 hover:bg-bone/40 cursor-pointer" onClick={() => toggle(l.id)} data-testid={`loan-ledger-row-${l.id}`}>
                                                    <td className="px-4 py-3 capitalize font-medium text-ink">{l.type || "loan"}{l.reason && <div className="text-xs text-ink/50 font-normal mt-0.5 truncate max-w-[260px]">{l.reason}</div>}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums">{fmt(l.amount)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">{fmt(l.monthly_installment)}</td>
                                                    <td className="px-4 py-3 text-right hidden md:table-cell">{(l.type === "advance" ? "Single" : `${l.tenure_months}m`)}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums">{fmt(l.balance)}</td>
                                                    <td className="px-4 py-3"><StatusPill status={l.status} /></td>
                                                    <td className="px-4 py-3 hidden lg:table-cell text-ink/60 text-xs">{(l.created_at || "").slice(0, 10)}</td>
                                                    <td className="px-2 py-3 text-right text-ink/50 text-xs">{isOpen ? "▼" : "▶"}</td>
                                                </tr>
                                                {isOpen && (l.installments || []).length > 0 && (
                                                    <tr key={`${l.id}-installments`} className="bg-bone/40">
                                                        <td colSpan={8} className="px-4 py-4">
                                                            <div className="text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Installment schedule</div>
                                                            <table className="w-full text-xs">
                                                                <thead>
                                                                    <tr className="text-ink/50">
                                                                        <th className="text-left py-1.5">Period</th>
                                                                        <th className="text-right py-1.5">Amount</th>
                                                                        <th className="text-left py-1.5">Status</th>
                                                                        <th className="text-left py-1.5">Paid at</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {l.installments.map((inst, idx) => (
                                                                        <tr key={`${l.id}-inst-${idx}`} className="border-t border-ink/10">
                                                                            <td className="py-1.5">{inst.period || "—"}</td>
                                                                            <td className="py-1.5 text-right tabular-nums">{fmt(inst.amount)}</td>
                                                                            <td className="py-1.5">{inst.paid ? <span className="text-emerald-700">Paid</span> : <span className="text-ink/50">Scheduled</span>}</td>
                                                                            <td className="py-1.5 text-ink/60">{(inst.paid_at || "").slice(0, 10) || "—"}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
